(ns common.schema.entry-criteria.utils
  "Pure cljc validator: given a resolved per-stage creator-config and the
   current opportunity/target/target-profile data, returns the set of entry
   criteria that block moving the opportunity into the target stage.

   See docs/functional/stage_entry_criteria/ for the design.

   The validator is data-in / data-out — no DOM, no Datomic calls, no re-frame.
   Field validation and write-permission are injected as fns so the same code
   can run on the frontend (re-frame state) and later on the backend (Datomic)."
  (:require [common.schema.entity-creator.utils :as ecu]
            [common.schema.entry-criteria.model :as model]
            [common.util :as cu]))

(defn mandatory-at-stage?
  "Resolved field/association configs carry `:stages {<stage-id> {:setting ...}}`.
   Mandatory at the given stage iff that map's setting is `:mandatory`."
  [config stage-id]
  (= :mandatory (get-in config [:stages stage-id :setting])))

(defn field-criteria
  "Walk a resolved per-scope fields-stage-config and emit field-blank /
   field-invalid criteria for the target stage. Write-disabled fields are
   reported with `:write-disabled? true` rather than filtered out."
  [{:keys [scope entity fields-config target-stage-id
           write-access? validate-field-fn]}]
  (->> (:fields fields-config)
       (mapcat (fn [field-config]
                 (let [attribute (:attribute field-config)
                       mandatory (mandatory-at-stage? field-config target-stage-id)
                       value     (get entity attribute)
                       blank     (cu/blank-value? value)
                       base      {:scope scope
                                  :attribute attribute
                                  :write-disabled? (not (write-access? scope attribute))}]
                   (cond
                     (and mandatory blank)
                     [(assoc base :kind :field-blank)]

                     (not blank)
                     (when-let [reason (validate-field-fn scope attribute value)]
                       [(assoc base :kind :field-invalid :reason reason)])))))))

(defn group-criteria
  "Walks the cljc-declared `ecu/custom-validations` for the scope's
   entity-type and emits one `:group-blank` criterion per failing rule. The
   criterion carries the participating attributes so the filter can surface
   them all in the modal — fill any one and the rule clears.

   A group is reported `:write-disabled?` only when every participating
   attribute is write-disabled (i.e. the user can't satisfy the rule); if
   any participating field is writable, the user has a way out."
  [{:keys [scope entity-type entity fields-config target-stage-id write-access?]}]
  (->> (ecu/failed-custom-validations entity-type
                                      entity
                                      {:fields-config fields-config
                                       :stage-id target-stage-id})
       (mapv (fn [{:keys [kind attributes error-key]}]
               {:scope scope
                :kind :group-blank
                :group-kind kind
                :error-key error-key
                :attributes attributes
                :write-disabled? (every? (fn [a]
                                           (not (write-access? scope a)))
                                         attributes)}))))

(defn association-criteria
  "Walk the resolved associations-stage-config and emit association-missing
   criteria for the target stage. Each association config item carries an
   `:association-key` `[source-entity-type other-entity-type association-type]`
   and a `:scope` (injected by the cljs wrapper from the source-entity-type
   relative to the opportunity / target / target-profile).

   Items whose source-entity-type doesn't match the current opportunity-/
   target-/target-profile-type — i.e. `:scope` is nil — are dropped. These
   are phantom associations left over from configuration drift (e.g. a sales-
   pipeline that used to be investor-only is now `target-asset-types
   #{:entity.type/company}`; the previously-configured investor-anchored
   associations no longer apply). Same filter the opportunity-creator's
   `resolve-association-entity-types` applies.

   `associations-by-scope` is a map of
   `{<scope> [pulled :entity.type/entity-association ...]}` so the validator
   stays pure."
  ;; v1: associations are reported as `:write-disabled? false` unconditionally
  ;; (write-access? is not consulted). field-write-access? doesn't naturally
  ;; extend to association-types; add a dedicated check if/when per-association
  ;; permission gating is needed.
  [{:keys [associations-config target-stage-id associations-by-scope
           _write-access?]}]
  (->> (:associations associations-config)
       (remove (comp nil? :scope))
       (filter #(mandatory-at-stage? % target-stage-id))
       (keep (fn [{:keys [association-key scope] :as _assoc-config}]
               (let [association-type (nth association-key 2 nil)
                     present? (->> (get associations-by-scope scope)
                                   (some #(= (:entity-association/type %)
                                             association-type)))]
                 (when-not present?
                   {:scope scope
                    :kind :association-missing
                    :association-key association-key
                    :association-type association-type
                    :write-disabled? false}))))))

(defn criteria-field-set
  "The set of attributes the modal should surface for a given scope's criteria:
   each single-field criterion's `:attribute` plus every participating attribute
   of any `:group-blank` criterion. Ignores `:association-missing` (associations
   are rendered in their own block)."
  [scope-criteria]
  (->> scope-criteria
       (mapcat (fn [{:keys [kind attribute attributes]}]
                 (cond
                   attribute              [attribute]
                   (= kind :group-blank)  attributes)))
       set))

(defn filter-fields-config
  "Returns a copy of `fields-config` with `:fields` trimmed to only those whose
   `:attribute` is in `must-show-attrs`. Field order is preserved so any
   always-first injections (e.g. person's name/email via
   `common.schema.entity-creator.utils/static-fields`) stay at the top."
  [fields-config must-show-attrs]
  (when fields-config
    (update fields-config :fields
            (fn [fields]
              (filterv #(contains? must-show-attrs (:attribute %)) fields)))))

(defn resolve-stage-entry-criteria
  "Returns a sequence of unmet entry criteria for moving the given opportunity
   into the given target stage. Each criterion item carries enough information
   for the modal (and a future backend gate) to render or enforce it.

   Inputs:
   - `:opportunity` / `:target` / `:target-profile` — pulled entity-maps for
     each scope (target / target-profile may be nil if not applicable).
   - `:target-stage-id` — the stage we're trying to move into.
   - `:opportunity-fields-stage-config`, `:target-fields-stage-config`,
     `:target-profile-fields-stage-config` — the per-scope outputs of
     `common.schema.entity-creator.utils/resolve-fields-stage-config`. Pass
     nil/missing for scopes that don't apply.
   - `:associations-stage-config` — the output of
     `resolve-associations-stage-config`. One block, may interleave scopes.
   - `:associations-by-scope` — pulled `:entity.type/entity-association`
     entities, keyed by scope.
   - `:write-access?` — `(fn [scope attr])` returning truthy iff the acting
     user has write/execute access. Defaults to always-true so backend reuse
     remains opt-in.
   - `:validate-field-fn` — `(fn [scope attr value])` returning a reason
     keyword/data when the value fails entity-schema validation, nil
     otherwise. Defaults to a no-op for v1 (UI is the gate); callers can
     inject `common.schema.constraints/validate-field` wrapped to the right
     shape.

   Each criterion item:
   - `:scope` — `:opportunity | :target | :target-profile`
   - `:kind`  — `:field-blank | :field-invalid | :group-blank | :association-missing`
   - `:attribute` — field attribute (single-field criteria)
   - `:attributes` / `:group-kind` / `:error-key` — group criteria
   - `:association-key` / `:association-type` — association identifier
   - `:write-disabled?` — bool
   - `:reason` — for `:field-invalid`, the validation failure detail

   `:entity-types` is `{:opportunity-entity-type ..., :target-entity-type ...,
   :target-profile-entity-type ...}`; used to dispatch cross-field group
   validations declared in `common.schema.entity-creator.utils/custom-validations`."
  [{:keys [opportunity target target-profile target-stage-id
           opportunity-fields-stage-config
           target-fields-stage-config
           target-profile-fields-stage-config
           associations-stage-config
           associations-by-scope
           entity-types
           write-access?
           validate-field-fn]
    :or   {write-access?     (fn [_scope _attr] true)
           validate-field-fn (fn [_scope _attr _value] nil)}}]
  (let [scope-args (fn [scope entity fields-config]
                     {:scope scope
                      :entity entity
                      :fields-config fields-config
                      :target-stage-id target-stage-id
                      :write-access? write-access?
                      :validate-field-fn validate-field-fn})
        group-args (fn [scope entity entity-type fields-config]
                     {:scope scope
                      :entity entity
                      :entity-type entity-type
                      :fields-config fields-config
                      :target-stage-id target-stage-id
                      :write-access? write-access?})]
    (concat
     (field-criteria (scope-args :opportunity opportunity
                                 opportunity-fields-stage-config))
     (field-criteria (scope-args :target target
                                 target-fields-stage-config))
     (field-criteria (scope-args :target-profile target-profile
                                 target-profile-fields-stage-config))
     (group-criteria (group-args :opportunity opportunity
                                 (:opportunity-entity-type entity-types)
                                 opportunity-fields-stage-config))
     (group-criteria (group-args :target target
                                 (:target-entity-type entity-types)
                                 target-fields-stage-config))
     (group-criteria (group-args :target-profile target-profile
                                 (:target-profile-entity-type entity-types)
                                 target-profile-fields-stage-config))
     (association-criteria {:associations-config associations-stage-config
                            :target-stage-id target-stage-id
                            :associations-by-scope associations-by-scope
                            :write-access? write-access?}))))

(defn entry-criteria-mode
  "Mode of a creator-config main config. An absent or unknown value gives the
   default mode, so a config that was saved before the modes existed keeps its
   behavior, and a bad stored value can never stop a stage move. The backend
   refuses a bad value when the config is saved, see
   `invalid-entry-criteria-modes`."
  [config]
  (let [mode (:entry-criteria-mode config)]
    (if (cu/one-of? mode model/entry-criteria-modes)
      mode
      model/default-entry-criteria-mode)))

(def main-config-slots
  "The slots of a creator-config that hold the main config of an opportunity.
   `:entry-criteria-mode` belongs in these and nowhere else."
  #{:main-config :single-entity-config})

(defn mode-entries
  "Each `:entry-criteria-mode` in a parsed creator-config, as `[path value]`.
   The path ends with the key itself."
  [creator-config]
  (letfn [(walk [path node]
            (when (map? node)
              (mapcat (fn [[k v]]
                        (if (= :entry-criteria-mode k)
                          [[(conj path k) v]]
                          (walk (conj path k) v)))
                      node)))]
    (vec (walk [] creator-config))))

(defn invalid-entry-criteria-modes
  "The `:entry-criteria-mode` entries of a parsed creator-config that cannot
   apply: a value that is not one of `model/entry-criteria-modes`, or a key in a
   slot that is not a main config (it would do nothing, and the editor never
   writes it there). A nil value is the same as no value: the mode is not set,
   and the default applies, so a nil is never reported. Returns
   `[{:path :value} ...]`, empty when the config is valid. The backend calls
   this when a pipeline's creator-config is saved."
  [creator-config]
  (->> (mode-entries creator-config)
       (remove (fn [[_path value]]
                 (nil? value)))
       (remove (fn [[path value]]
                 (and (cu/one-of? (peek (pop path)) main-config-slots)
                      (cu/one-of? value model/entry-criteria-modes))))
       (mapv (fn [[path value]]
               {:path path
                :value value}))))

(defn shown-at-stage?
  "True when a resolved field or association config shows the item at the
   stage, as mandatory or optional."
  [config stage-id]
  (contains? ecu/visible-settings (get-in config [:stages stage-id :setting])))

(defn review-attributes
  "Attributes of one scope that the review shows: each field that the config
   shows at the target stage, in config order. A field that the user cannot
   write is left out, because the modal cannot show it as an input. A scope
   without an entity has nothing to show."
  [{:keys [scope entity fields-config target-stage-id write-access?]}]
  (when entity
    (->> (:fields fields-config)
         (filter (fn [field-config]
                   (shown-at-stage? field-config target-stage-id)))
         (map :attribute)
         (filter (fn [attribute]
                   (write-access? scope attribute)))
         vec)))

(defn review-associations
  "Optional associations that the config shows at the target stage and that the
   scope entity does not have yet. A missing mandatory association is already a
   criterion, so the review leaves it out. The modal can only add an
   association, so an association that exists is not listed.
   `scope-entities` is `{<scope> entity}`. A scope without an entity has no row."
  [{:keys [associations-config target-stage-id associations-by-scope scope-entities]}]
  (->> (:associations associations-config)
       (filter (fn [{:keys [scope]}]
                 (some? (get scope-entities scope))))
       (filter (fn [association-config]
                 (= :optional (get-in association-config
                                      [:stages target-stage-id :setting]))))
       (keep (fn [{:keys [association-key scope]}]
               (let [association-type (nth association-key 2 nil)
                     present? (->> (get associations-by-scope scope)
                                   (some (fn [association]
                                           (= association-type
                                              (:entity-association/type association)))))]
                 (when-not present?
                   {:scope scope
                    :association-key association-key
                    :association-type association-type}))))
       vec))

(defn review-for-stage
  "Decides whether the modal shows a review for a stage move, and what it shows.
   It does not decide whether the move is blocked. `resolve-stage-entry-criteria`
   does that, and its result is the `:criteria` input.

   Mode `:missing-mandatory` never has a review. Mode `:missing-any` has one
   when a criterion is unmet, a shown field is blank, or an optional
   association is missing. Mode `:always` has one whenever the config shows
   something at the stage. When the modal opens in these two modes it shows all
   of it, also the fields that are filled.

   Takes the same entity, config and `:write-access?` inputs as
   `resolve-stage-entry-criteria`, plus `:mode` and the `:criteria` that it
   returned. The entities must be the state after the submitted edits.
   `:associations-stage-config` must carry `:scope` on each item.

   Returns nil, or `{:mode :attributes :associations}`. `:attributes` is
   `{<scope> [attribute ...]}` and lists only a scope that has some.
   `:associations` is the result of `review-associations`."
  [{:keys [mode criteria target-stage-id
           opportunity target target-profile
           opportunity-fields-stage-config
           target-fields-stage-config
           target-profile-fields-stage-config
           associations-stage-config
           associations-by-scope
           write-access?]
    :or {write-access? (fn [_scope _attribute] true)}}]
  (when (cu/one-of? mode model/review-modes)
    (let [scope-entities {:opportunity opportunity
                          :target target
                          :target-profile target-profile}
          attributes (->> {:opportunity opportunity-fields-stage-config
                           :target target-fields-stage-config
                           :target-profile target-profile-fields-stage-config}
                          (keep (fn [[scope fields-config]]
                                  (let [scope-attributes (review-attributes
                                                          {:scope scope
                                                           :entity (get scope-entities scope)
                                                           :fields-config fields-config
                                                           :target-stage-id target-stage-id
                                                           :write-access? write-access?})]
                                    (when (seq scope-attributes)
                                      [scope scope-attributes]))))
                          (into {}))
          associations (review-associations
                        {:associations-config associations-stage-config
                         :target-stage-id target-stage-id
                         :associations-by-scope associations-by-scope
                         :scope-entities scope-entities})
          blank-attribute? (fn [[scope scope-attributes]]
                             (some (fn [attribute]
                                     (cu/blank-value?
                                      (get (get scope-entities scope) attribute)))
                                   scope-attributes))
          something-missing? (boolean
                              (or (seq criteria)
                                  (some blank-attribute? attributes)
                                  (seq associations)))
          something-to-show? (boolean
                              (or (seq attributes)
                                  (seq associations)))]
      (when (and something-to-show?
                 (or (= mode :always)
                     something-missing?))
        {:mode mode
         :attributes attributes
         :associations associations}))))
